// Shared types for user dashboard
// Serve the purpose of defining an interface between the web and api

import type { UserAccessResult } from "../userAccess"

export type UserProductPublishStatus =
  | "DRAFT"
  | "PUBLISHED"
  | "PENDING"
  | "REJECTED"
  | "REMOVED"

type LengthUnit = "INCH" | "CENTIMETER" | "MILLIMETER"

export type LabelSize = {
  height: number
  width: number
  unit: LengthUnit
}

export type LabelTransitionStatus =
  | "outdated"
  | "readyToMigrate"
  | "readyForReview"
  | "migrated"
  | null

export type UserProductListItem = {
  id: string
  productSettingsId: string
  name: string
  baseId: string | null
  updatedAt: Date
  imageUrl: string | null
  isPrePaidProduct: boolean
  availablePrepaidUnits: number
  priceSuplifulCharged: number
  priceRetail: number | null
  publishStatus: UserProductPublishStatus
  labelTransitionStatus: LabelTransitionStatus
  canOpenLabelEditor: boolean
  suplifulProduct: {
    restockDate: Date | null
    canCreate: UserAccessResult
  }
  /**
   * Whether the product is published. Separate from `publishedStores`, which
   * answers *where* it is live: off the publication flag the stores come from
   * the legacy `UserProduct.storeId` scalar, which outlives the publication it
   * described, so the two can disagree and this one is the publish state.
   */
  isPublished: boolean
  /**
   * Stores the product is currently live in, in a stable order that is not
   * the order it was published to them.
   */
  publishedStores: PublishedStore[]
  isSingleSkuBundle: boolean
  singleSkuBundleQuantity: number | null
}

export type UserStorePlatform =
  | "SHOPIFY"
  | "TIKTOK"
  | "CLICKFUNNELS"
  | "WOOCOMMERCE"
  | "WIX"
  | "AMAZON"
  | "SUPLIFUL"
  | "API"

/** One store a product is live in, as every product view names it. */
export type PublishedStore = {
  id: string
  name: string | null
  platform: UserStorePlatform
}

export type UserProductFile = {
  id: string
  kind: string
  fileName: string
  url: string
}

export type MyProduct = {
  id: string
  userId: string
  name: string
  /**
   * Whether the product is published. Separate from `publishedStores`, which
   * answers *where* it is live: off the publication flag the stores come from
   * the legacy `UserProduct.storeId` scalar, which outlives the publication it
   * described, so the two can disagree and this one is the publish state.
   */
  isPublished: boolean
  /**
   * Stores the product is currently live in, in a stable order that is not
   * the order it was published to them.
   */
  publishedStores: PublishedStore[]
  customizationPreviewImage: string | null
  coverImageId: string | null
  nutritionFactsImage: string | null
  description: string
  price: number
  priceSuplifulCharged: number
  suplifulProduct: {
    sku: string
    slug: string
    isFulfillmentReady: boolean
    /** Current label size, sourced from the Sanity `labelSizeV2` field. */
    labelSize: LabelSize | null
  }
  files: UserProductFile[]
  label: {
    previewImageUrl: string | null
    createdAt: Date
    verifiedAt: Date | null
  } | null
  labelDraft: { updatedAt: Date } | null
  canOpenLabelEditor: boolean
  labelTransitionStatus: LabelTransitionStatus
  labelTransitionIsLoading: boolean
  hasUnpushedImageChanges: boolean
  isSingleSkuBundle: boolean
  singleSkuBundleQuantity: number | null
  customSkuId: string | null
}

/**
 * User store representation for dashboard and listing
 * Includes computed fields for connection status and scope validation
 */
export type UserStore = {
  id: string
  platform: UserStorePlatform
  name: string | null
  shopUrl: string
  /** Computed field: checks if store is properly connected based on platform-specific logic */
  isConnected: boolean
  /** Computed field: for Shopify stores, checks if OAuth scopes match requirements */
  scopesAccepted: boolean
}

export type SubscriptionItem = {
  id: string
  price: number
  name: string
  status: "ACTIVE" | "CANCELED" | "NOT_PURCHASED"
  icon: "headphones" | "trendup" | "megaphone"
}

/**
 * Access control error codes used for user dashboard
 */
export type AccessErrorCode =
  | "EMAIL_VERIFICATION_REQUIRED"
  | "SUBSCRIPTION_REQUIRED"
  | "FREE_TRIAL_LIMIT"
  | "PRODUCT_OUT_OF_STOCK"
  | "PRODUCT_SOON_TO_BE_DISCONTINUED"
  | "SUBSCRIPTION_UPGRADE_REQUIRED"
  | "STORE_REMOVED"
  | "UNAUTHORIZED"
  | "BILLING_DETAILS_REQUIRED"
  | "UNPAID_INVOICES"
  | "STORE_CONNECTED_TO_ANOTHER_USER"

export type OrderKind =
  | "SAMPLE"
  | "MANUAL"
  | "STOREFRONT"
  | "SHOPIFY"
  | "TIKTOK"
  | "WOOCOMMERCE"
  | "WIX"
  | "AMAZON"
  | "FBA"
  | "BULK"
  | "CLICKFUNNELS"
  | "API"

export const orderStatus = [
  "NEW",
  "PROCESSING",
  "ACCEPTED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELED",
  "RETURNED",
] as const

export type OrderStatus = (typeof orderStatus)[number]

export type OrderDetailLine = {
  id: string
  title: string
  imageUrl: string | null
  priceSupliful: number
  count: number
  sku: string
  restockDate: Date | null
  currency: string
  isRefunded: boolean
  isRemoved: boolean
  singleSkuBundleQuantity: number | null
}

export type UserSubscriptionTier = "TIER_20" | "TIER_30"

/** Tier as stored in the database, including the withdrawn Base plan */
export type StoredUserSubscriptionTier = UserSubscriptionTier | "TIER_10"

export type UserSubscriptionBillingInterval = "MONTH" | "YEAR"

export type UserPaymentProcessor = "STRIPE" | "SHOPIFY"

export type UserSubscriptionStatus = "ACTIVE" | "PAUSED" | "CANCELLED"

export type UserSubscription = {
  tier: UserSubscriptionTier
  interval: UserSubscriptionBillingInterval
  status: UserSubscriptionStatus
  currentPeriodEnd: Date | null
  endedAt: Date | null
  paymentProcessor: UserPaymentProcessor
  /** Recurring charge in USD per `interval`; null when the Stripe price could not be resolved */
  price: number | null
  canCancel: boolean
  userStoreId?: string
}

/**
 * Notification reasons that users can opt out of receiving emails for
 */
export const NotificationReason = {
  PaymentFailed: "PAYMENT_FAILED",
  FulfillmentRequestRejected: "FULFILLMENT_REQUEST_REJECTED",
  OrderShipped: "ORDER_SHIPPED",
  ProductOutOfStock: "PRODUCT_OUT_OF_STOCK",
  StorefrontOrderConfirmation: "STOREFRONT_ORDER_CONFIRMATION",
  StorefrontOrderShipped: "STOREFRONT_ORDER_SHIPPED",
  WoocommerceOrderImportedPartially: "WOOCOMMERCE_ORDER_IMPORTED_PARTIALLY",
  WoocommerceOrderRejected: "WOOCOMMERCE_ORDER_REJECTED",
} as const

export type NotificationReason =
  (typeof NotificationReason)[keyof typeof NotificationReason]

/**
 * Social networks available for user profiles
 */
export type SocialNetwork =
  | "INSTAGRAM"
  | "FACEBOOK"
  | "TIKTOK"
  | "TWITCH"
  | "SNAPCHAT"
  | "OTHER"

export const socialNetworks = [
  "INSTAGRAM",
  "FACEBOOK",
  "TIKTOK",
  "TWITCH",
  "SNAPCHAT",
  "OTHER",
] as const

/**
 * Reasons why a payment method cannot be removed
 */
export type PaymentMethodRemovalBlockReason =
  | "IS_DEFAULT_WITH_MULTIPLE_METHODS"
  | "REQUIRES_SUPLIFUL_CREDITS"
  | "HAS_ACTIVE_STORE_OR_PRODUCTS"

export type PaymentMethodCanRemove =
  | { canRemove: true }
  | { canRemove: false; removalBlockReason: PaymentMethodRemovalBlockReason }

export type PaymentMethod = {
  id: string
  name: string
  brand?: string
  last4?: string
  isDefault: boolean
} & PaymentMethodCanRemove

export type PublishChecklistItemKey =
  | "PDF_LABEL"
  | "STORE_SETUP"
  | "PAYMENT_METHOD"
  | "EMAIL_VERIFICATION"
  | "SUBSCRIPTION"

export type PublishChecklistItem = {
  key: PublishChecklistItemKey
  done: boolean
}

export type OnHoldReason =
  | "OUT_OF_STOCK"
  | "FAILED_PAYMENT"
  | "UNVERIFIED_LABEL"
  | "MISSING_EORI_NUMBER"
  | "TECH_BUG"
  | "PRE_ORDER"
  | "LABEL_ISSUE"
  | "FBA_PAYMENT"
  | "FBA_STOCK"
  | "FBA_SHIPPING_LABEL"
  | "FBA_SHIPPING_LABEL_VERIFYING"
  | "FBA_POST_PAYMENT_DUE_DATE"
  | "BULK_PAYMENT"
  | "BULK_STOCK"
  | "MISSING_SUBSCRIPTION"
  | "PENDING_V2_FULFILLMENT"
  | "MISSING_V2_LABEL"
  | "INVALID_ADDRESS"
